import { Flame, Droplets, Wind, Mountain } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export const ElementsGrid = () => (
  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
    <div className="text-center space-y-2 p-4 rounded-lg bg-gradient-to-br from-red-50 to-orange-50">
      <div className="flex justify-center"><Flame className="w-8 h-8 text-red-600" /></div>
      <h3 className="font-semibold text-gray-900">Fire</h3>
      <p className="text-xs text-gray-600">Drive & Direction</p>
    </div>
    <div className="text-center space-y-2 p-4 rounded-lg bg-gradient-to-br from-blue-50 to-cyan-50">
      <div className="flex justify-center"><Droplets className="w-8 h-8 text-blue-600" /></div>
      <h3 className="font-semibold text-gray-900">Water</h3>
      <p className="text-xs text-gray-600">Care & Connection</p>
    </div>
    <div className="text-center space-y-2 p-4 rounded-lg bg-gradient-to-br from-teal-50 to-emerald-50">
      <div className="flex justify-center"><Wind className="w-8 h-8 text-teal-600" /></div>
      <h3 className="font-semibold text-gray-900">Wind</h3>
      <p className="text-xs text-gray-600">Clarity & Adaptability</p>
    </div>
    <div className="text-center space-y-2 p-4 rounded-lg bg-gradient-to-br from-amber-50 to-yellow-50">
      <div className="flex justify-center"><Mountain className="w-8 h-8 text-amber-700" /></div>
      <h3 className="font-semibold text-gray-900">Earth</h3>
      <p className="text-xs text-gray-600">Stability & Structure</p>
    </div>
  </div>
);

export const WhatToExpect = () => (
  <div className="bg-gradient-to-br from-orange-50 to-yellow-50 p-6 rounded-xl space-y-3 border border-[#FFD04A]/30">
    <h3 className="text-lg font-semibold text-gray-900">What to Expect</h3>
    <ul className="space-y-2 text-sm text-gray-700">
      <li className="flex items-start"><span className="mr-2">•</span><span>5 realistic scenarios reflecting professional challenges</span></li>
      <li className="flex items-start"><span className="mr-2">•</span><span>3 stages per scenario to track your decision-making flow</span></li>
      <li className="flex items-start"><span className="mr-2">•</span><span>10-15 minutes to complete</span></li>
      <li className="flex items-start"><span className="mr-2">•</span><span>Personalized downloadable report at the end</span></li>
    </ul>
  </div>
);

const SelectField = ({ id, testid, label, value, onChange, options, placeholder }) => (
  <div className="space-y-2">
    <Label htmlFor={id} className="text-base">{label} *</Label>
    <select
      id={id}
      data-testid={testid}
      value={value}
      onChange={onChange}
      className="h-12 w-full rounded-md border border-gray-300 bg-white px-3 text-base focus:outline-none focus:ring-2 focus:ring-indigo-500"
    >
      <option value="">{placeholder}</option>
      {options.map(opt => (
        <option key={opt.value} value={opt.value}>{opt.label}</option>
      ))}
    </select>
  </div>
);

const GENDER_OPTIONS = [
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
  { value: 'non-binary', label: 'Non-binary' },
  { value: 'prefer-not-to-answer', label: 'Prefer not to answer' },
];
const AGE_OPTIONS = ['18-24', '25-34', '35-44', '45-54', '55-64', '65-80'].map(v => ({ value: v, label: v }));
const EXPERIENCE_OPTIONS = ['0-5', '6-10', '11-15', '16-20', '21-30', '31-40', '41-60'].map(v => ({ value: v, label: `${v} years` }));

export const ParticipantForm = ({
  userName, setUserName,
  gender, setGender,
  ageRange, setAgeRange,
  experience, setExperience,
  loading, onSubmit,
}) => (
  <>
    <div className="space-y-4 max-w-md mx-auto">
      <div className="space-y-2">
        <Label htmlFor="name" className="text-base">Your Name *</Label>
        <Input
          id="name"
          data-testid="name-input"
          placeholder="Enter your name"
          value={userName}
          onChange={(e) => setUserName(e.target.value)}
          className="h-12 text-base"
        />
      </div>
      <SelectField id="gender" testid="gender-select" label="Gender"
        value={gender} onChange={(e) => setGender(e.target.value)}
        options={GENDER_OPTIONS} placeholder="Select gender" />
      <SelectField id="age" testid="age-select" label="Age Range"
        value={ageRange} onChange={(e) => setAgeRange(e.target.value)}
        options={AGE_OPTIONS} placeholder="Select age range" />
      <SelectField id="experience" testid="experience-select" label="Years of Experience"
        value={experience} onChange={(e) => setExperience(e.target.value)}
        options={EXPERIENCE_OPTIONS} placeholder="Select experience" />
    </div>

    <div className="flex justify-center pt-4">
      <Button
        data-testid="start-assessment-btn"
        onClick={onSubmit}
        disabled={loading}
        className="h-14 px-12 text-lg bg-gradient-to-r from-[#FF751F] to-[#FFBD25] hover:from-[#FF751F] hover:to-[#FFD04A] text-white shadow-lg font-semibold"
      >
        {loading ? "Starting..." : "Begin Assessment"}
      </Button>
    </div>
  </>
);
